import React, { useState, useContext, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip, Button, Popover, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import AssessmentIcon from '@material-ui/icons/Assessment';
import LanguageIcon from '@material-ui/icons/Language';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SettingsIcon from '@material-ui/icons/Settings';
import NotificationsIcon from '@material-ui/icons/Notifications';
import GetAppIcon from '@material-ui/icons/GetApp';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import ScheduleIcon from '@material-ui/icons/Schedule';
import DashboardIcon from '@material-ui/icons/Dashboard';
import MapIcon from '@material-ui/icons/Map';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import AssignmentIcon from '@material-ui/icons/Assignment';
import TuneIcon from '@material-ui/icons/Tune';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import BuildIcon from '@material-ui/icons/Build';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import FilterListIcon from '@material-ui/icons/FilterList';
import ChatDrawer from '../Chat/ChatDrawer';
import CloseIcon from '@material-ui/icons/Close';
import { isPetrotecTenant, isMoveExpressTenant } from '../../utils/manageHelpers';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { getSocket } from '../../utils/websocket';
import { setLang, setMapProvider, setShowReports, setDisplayedSavedRoutes, setShowDetails, setSidebarTab, setActiveView, setSidebarVisible, selectUI } from '../../store/slices/uiSlice';
import { loadRoute, fetchRouteDetail } from '../../store/slices/routesSlice';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectAlerts, selectWSStatus, addAlert } from '../../store/slices/notificationsSlice';
import { t } from '../../i18n';
import { calculateETA } from '../../utils/tachograph';
import ModuleGate from '../common/ModuleGate';
import ManageDialog from '../common/ManageDialog';
import AppDownloadModal from '../common/AppDownloadModal';
import PreferencesDialog from '../Preferences/PreferencesDialog';
import { version } from '../../../package.json';
import { AuthContext } from '../../App';

const useStyles = makeStyles(() => ({
  // ── Temas da BARRA PRINCIPAL (só a toolbar): Clássico / Claro / Escuro.
  // Tokens --tb-* definidos por data-tb-theme no root da barra; as classes
  // abaixo consomem-nos. Reutiliza a paleta da identidade ecooGPS do backoffice
  // (gradiente ciano→menta, claro #0b1a2c/#fff, escuro navy). Clássico = atual.
  '@global': {
    '[data-tb-theme="classic"]': {
      '--tb-bg': 'linear-gradient(90deg, #1d3f58 0%, #1a5a72 35%, #1a8a6a 65%, #27ae78 100%)',
      '--tb-border': 'rgba(255,255,255,0.06)',
      '--tb-shadow': '0 4px 16px rgba(0,0,0,0.35)',
      '--tb-fg': '#ffffff',
      '--tb-fg-dim': 'rgba(255,255,255,0.55)',
      '--tb-fg-mute': 'rgba(255,255,255,0.38)',
      '--tb-hover-fg': 'rgba(255,255,255,0.85)',
      '--tb-hover-bg': 'rgba(255,255,255,0.08)',
      '--tb-active-fg': '#ffffff',
      '--tb-active-bg': 'rgba(255,255,255,0.14)',
      '--tb-active-bg-hover': 'rgba(255,255,255,0.18)',
      '--tb-divider': 'rgba(255,255,255,0.10)',
      '--tb-chip-bg': 'rgba(255,255,255,0.07)',
      '--tb-chip-border': 'rgba(255,255,255,0.12)',
    },
    // Tokens alinhados ao ecoogps-ui-kit (ecoogps-tokens.css): claro tx-1 #0B1A2C /
    // tx-2 #3A4E66 / accent #0677A6 / cyan #0796CC / line-2 rgba(22,58,96,0.12).
    '[data-tb-theme="light"]': {
      '--tb-bg': '#f5f8fb',
      '--tb-border': 'rgba(22,58,96,0.12)',
      '--tb-shadow': '0 2px 14px rgba(20,42,70,0.12)',
      '--tb-fg': '#0b1a2c',
      '--tb-fg-dim': '#3a4e66',
      '--tb-fg-mute': '#8296ad',
      '--tb-hover-fg': '#0b1a2c',
      '--tb-hover-bg': 'rgba(7,150,204,0.06)',
      '--tb-active-fg': '#0677a6',
      '--tb-active-bg': 'rgba(7,150,204,0.10)',
      '--tb-active-bg-hover': 'rgba(7,150,204,0.16)',
      '--tb-divider': 'rgba(22,58,96,0.12)',
      '--tb-chip-bg': 'rgba(7,150,204,0.06)',
      '--tb-chip-border': 'rgba(22,58,96,0.12)',
    },
    // Escuro alinhado ao kit: bg-1/2 #08111D/#0D1828, tx-1 #EAF3FB / tx-2 #A9BED3,
    // cyan #20C6F4, line-2 rgba(110,193,228,0.14).
    '[data-tb-theme="dark"]': {
      '--tb-bg': 'linear-gradient(90deg, #08111d 0%, #0d1828 100%)',
      '--tb-border': 'rgba(110,193,228,0.14)',
      '--tb-shadow': '0 4px 16px rgba(0,0,0,0.5)',
      '--tb-fg': '#eaf3fb',
      '--tb-fg-dim': '#a9bed3',
      '--tb-fg-mute': 'rgba(169,190,211,0.5)',
      '--tb-hover-fg': '#eaf3fb',
      '--tb-hover-bg': 'rgba(255,255,255,0.06)',
      '--tb-active-fg': '#eaf3fb',
      '--tb-active-bg': 'rgba(32,198,244,0.18)',
      '--tb-active-bg-hover': 'rgba(32,198,244,0.26)',
      '--tb-divider': 'rgba(110,193,228,0.14)',
      '--tb-chip-bg': 'rgba(110,193,228,0.08)',
      '--tb-chip-border': 'rgba(110,193,228,0.18)',
    },
  },
  header: {
    position: 'relative',
    height: 42,
    background: 'var(--tb-bg, linear-gradient(90deg, #1d3f58 0%, #1a5a72 35%, #1a8a6a 65%, #27ae78 100%))',
    borderBottom: '1px solid var(--tb-border, rgba(255,255,255,0.06))',
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px 0 6px',
    gap: 0,
    zIndex: 1200,
    flexShrink: 0,
    boxShadow: 'var(--tb-shadow, 0 4px 16px rgba(0,0,0,0.35))',
    borderRadius: 16,
    margin: '6px 8px 0',
    transition: 'background 0.25s ease, box-shadow 0.25s ease',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginRight: 16,
    flexShrink: 0,
  },
  logoIcon: {
    width: 30,
    height: 30,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(34,197,94,0.35)',
    flexShrink: 0,
  },
  logoText: {
    lineHeight: 1,
    '& .eco': {
      fontSize: '0.95rem',
      fontWeight: 300,
      color: 'var(--tb-fg, #ffffff)',
      letterSpacing: '0.01em',
    },
    '& .gps': {
      fontSize: '0.95rem',
      fontWeight: 700,
      color: 'var(--tb-fg, #ffffff)',
      letterSpacing: '0.02em',
    },
    '& .sub': {
      fontSize: '0.52rem',
      color: 'var(--tb-fg-mute, rgba(255,255,255,0.38))',
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      marginTop: 1,
    },
  },
  navTabs: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    marginRight: 12,
    // Encolhe antes de empurrar as acções da direita para fora do ecrã.
    // Com muitos separadores (ex.: módulo Administração) a área de navegação
    // passa a ter scroll horizontal (sem barra visível) em vez de esconder
    // os ícones da direita (menu, alertas, idioma, avatar).
    flexShrink: 1,
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  navTab: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    color: 'var(--tb-fg-dim, rgba(255,255,255,0.50))',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    '&:hover': {
      color: 'var(--tb-hover-fg, rgba(255,255,255,0.85))',
      background: 'var(--tb-hover-bg, rgba(255,255,255,0.08))',
    },
  },
  navTabActive: {
    color: 'var(--tb-active-fg, #ffffff)',
    background: 'var(--tb-active-bg, rgba(255,255,255,0.14))',
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    '&:hover': {
      color: 'var(--tb-active-fg, #ffffff)',
      background: 'var(--tb-active-bg-hover, rgba(255,255,255,0.18))',
    },
  },
  divider: {
    width: 1,
    height: 26,
    background: 'var(--tb-divider, rgba(255,255,255,0.10))',
    flexShrink: 0,
    marginRight: 14,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  actionIconBtn: {
    color: 'var(--tb-fg-dim, rgba(255,255,255,0.55))',
    padding: 7,
    '&:hover': { color: 'var(--tb-hover-fg, #ffffff)', background: 'var(--tb-hover-bg, rgba(255,255,255,0.08))' },
    '&:disabled': { color: 'var(--tb-fg-mute, rgba(255,255,255,0.2))' },
  },
  reportsBtn: {
    borderColor: 'var(--tb-chip-border, rgba(255,255,255,0.20))',
    color: 'var(--tb-fg-dim, rgba(255,255,255,0.75))',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    '&:hover': { borderColor: 'var(--tb-fg-dim, rgba(255,255,255,0.45))', background: 'var(--tb-hover-bg, rgba(255,255,255,0.06))' },
    '&.Mui-disabled': { borderColor: 'var(--tb-chip-border, rgba(255,255,255,0.10))', color: 'var(--tb-fg-mute, rgba(255,255,255,0.25))' },
  },
  sendVehicleBtn: {
    borderColor: 'rgba(59,130,246,0.45)',
    color: 'rgba(147,197,253,0.95)',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    '&:hover': { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.08)' },
    '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.25)' },
  },
  sentVehicleBtn: {
    borderColor: 'rgba(34,197,94,0.6)',
    color: '#4ade80',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    background: 'rgba(34,197,94,0.10)',
    '&.Mui-disabled': { borderColor: 'rgba(34,197,94,0.4)', color: '#4ade80', background: 'rgba(34,197,94,0.10)' },
  },
  sendBtn: {
    borderColor: 'rgba(34,197,94,0.45)',
    color: 'rgba(34,197,94,0.9)',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    '&:hover': { borderColor: '#22c55e', background: 'rgba(34,197,94,0.08)' },
    '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.25)' },
  },
  sentBtn: {
    borderColor: 'rgba(34,197,94,0.6)',
    color: '#4ade80',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    background: 'rgba(34,197,94,0.10)',
    '&.Mui-disabled': { borderColor: 'rgba(34,197,94,0.4)', color: '#4ade80', background: 'rgba(34,197,94,0.10)' },
  },
  phoneActiveBtn: {
    color: '#4ade80',
    padding: 7,
    '&:hover': { color: '#86efac', background: 'rgba(34,197,94,0.12)' },
  },
  versionBadge: {
    padding: '3px 9px',
    borderRadius: 12,
    background: 'var(--tb-chip-bg, rgba(255,255,255,0.07))',
    border: '1px solid var(--tb-chip-border, rgba(255,255,255,0.12))',
    color: 'var(--tb-fg-dim, rgba(255,255,255,0.50))',
    fontSize: '0.62rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    flexShrink: 0,
    marginLeft: 4,
  },
  langBtn: {
    color: 'var(--tb-fg-dim, rgba(255,255,255,0.55))',
    padding: '4px 6px',
    '&:hover': { color: 'var(--tb-hover-fg, #ffffff)', background: 'var(--tb-hover-bg, rgba(255,255,255,0.08))' },
  },
  appDownloadBtn: {
    borderColor: 'rgba(168,85,247,0.40)',
    color: 'rgba(216,180,254,0.9)',
    fontSize: '0.68rem',
    padding: '2px 8px',
    textTransform: 'none',
    fontWeight: 600,
    letterSpacing: '0.02em',
    '&:hover': { borderColor: '#a855f7', background: 'rgba(168,85,247,0.10)' },
  },
  rightDivider: {
    width: 1,
    height: 22,
    background: 'rgba(255,255,255,0.10)',
    flexShrink: 0,
    margin: '0 6px',
  },
  saveBtn: {
    borderColor: 'rgba(59,130,246,0.45)',
    color: 'rgba(147,197,253,0.95)',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': { borderColor: '#3b82f6', background: 'rgba(59,130,246,0.12)' },
    '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.25)' },
  },
  manageBtn: {
    borderColor: 'rgba(34,197,94,0.40)',
    color: 'rgba(134,239,172,0.90)',
    fontSize: '0.72rem',
    padding: '3px 10px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': { borderColor: '#22c55e', background: 'rgba(34,197,94,0.10)' },
  },
  popoverPaper: {
    background: '#ffffff',
    borderRadius: 14,
    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
    border: '1px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
    minWidth: 240,
    marginTop: 8,
  },
  menuList: {
    padding: '6px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      background: '#f1f5f9',
    },
  },
  menuItemDisabled: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  menuItemIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  menuItemText: {
    flex: 1,
  },
  menuItemTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#1e293b',
    lineHeight: 1.2,
  },
  menuItemDesc: {
    fontSize: '0.62rem',
    color: '#94a3b8',
    lineHeight: 1.3,
  },
  menuDivider: {
    height: 1,
    background: '#f1f5f9',
    margin: '4px 10px',
  },
  menuItemBadge: {
    fontSize: '0.52rem',
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
}));

function Header() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientName, userEmail, userRole, userPermission, clientExternalId, clientSettings, modules } = useContext(AuthContext);
  // Preferências escrevem no PERFIL da Organização → só um Gestor lá mexe.
  // O eixo é o permissionLevel (definido no backoffice), NÃO o role: todos os
  // utilizadores vindos do EcooGPS entram como 'admin', logo o role não filtra.
  const canEditPrefs = userRole === 'super_admin' || userPermission === 'manager';
  const { lang, activeView, mapProvider, operationOrigin } = useSelector(selectUI);
  const route = useSelector(selectCurrentRoute);

  // ── App do técnico: DERIVADO, não é módulo ─────────────────────────
  // Se nenhum tipo de OT usa a App (orderFlows[t].usesApp), então o menu ⋮
  // (só tem "descarregar a App" e o QR Code) e o chat com motoristas não têm
  // com quem falar — escondem-se. Deriva da config de Ordens, para não haver
  // dois interruptores para a mesma coisa.
  // O SINO fica SEMPRE: os alertas vêm do GPS do veículo (Traccar/EcooGPS),
  // não da App — escondê-lo tirava informação real a quem não usa App.
  const usesDriverApp = (() => {
    const flows = clientSettings?.orderFlows;
    if (!flows || Object.keys(flows).length === 0) return true; // fail-open
    return Object.values(flows).some(f => f?.usesApp !== false);
  })();

  // ── Menu = módulo ──────────────────────────────────────────────────
  // Cada botão do menu principal é um módulo do Perfil. Fail-open quando não
  // há lista de módulos (modo local/mock), igual ao ModuleGate.
  const hasMod = (key) => !key || !modules || modules.length === 0 || modules.includes(key);
  const navTabs = [
    { id: 'dashboard', icon: DashboardIcon, label: t(lang, 'navDashboard'), module: 'dashboard' },
    { id: 'planner', icon: MapIcon, label: t(lang, 'navPlanner'), module: 'route_planner' },
    { id: 'fleet', icon: LocalShippingIcon, label: t(lang, 'navFleet'), module: 'fleet' },
    { id: 'orders', icon: AssignmentIcon, label: t(lang, 'navOrders'), module: 'orders' },
    { id: 'manage', icon: SettingsIcon, label: lang === 'pt' ? 'Operações' : 'Operations', module: 'operations' },
    // Manutenção: hub (Clientes Finais, Postos, Equipamentos, SLAs, Materiais,
    // Códigos Contabilísticos, Custos & SLA). Aparece para a variante Petrotec
    // (comportamento histórico, intocado) OU para qualquer perfil com o módulo
    // 'maintenance' ligado — p.ex. a variante Mercadoria (Casais). O módulo é a
    // via limpa e escalável; o isPetrotecTenant fica como atalho da variante.
    ...((isPetrotecTenant(clientExternalId, clientSettings) || (Array.isArray(modules) && modules.includes('maintenance')))
      ? [{ id: 'maintenance', icon: BuildIcon, label: lang === 'pt' ? 'Manutenção' : 'Maintenance', module: null }]
      : []),
    { id: 'reports', icon: AssessmentIcon, label: t(lang, 'navReports'), module: 'reports' },
    // Administração (org_admin, manager) foi MOVIDA da barra para o MENU DO
    // AVATAR (JC) — menu de gestor pouco frequente que ficava cortado na barra
    // cheia. Passa a "ecrã interno" (ver lista `conhecido` abaixo).
  ].filter(tab => hasMod(tab.module));

  // Aterragem: se o ecrã actual pertence a um menu que este Perfil não tem,
  // manda para o 1º menu disponível. Evita o ecrã em branco seja qual for a
  // combinação de módulos (e cobre o Planeamento ter deixado de ser base).
  useEffect(() => {
    if (!modules || modules.length === 0 || navTabs.length === 0) return;
    // Ecrãs internos (ex.: sub-ecrãs de Operações) não estão na barra — só
    // reagimos quando o activeView É um separador conhecido mas foi removido.
    // 'org_admin' NÃO está aqui de propósito: é agora um ecrã interno (aberto
    // pelo menu do avatar), por isso o efeito não deve redirecioná-lo p/ a barra.
    const conhecido = ['dashboard', 'planner', 'fleet', 'orders', 'manage', 'reports'].includes(activeView);
    if (!conhecido) return;
    if (navTabs.some(t => t.id === activeView)) return;
    dispatch(setActiveView(navTabs[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, modules]);

  // Tema da BARRA PRINCIPAL (só a toolbar): 'classic' (atual) | 'light' | 'dark'.
  // Persiste por browser (localStorage); default clássico.
  const [toolbarTheme, setToolbarTheme] = useState(() => {
    try { return localStorage.getItem('plannerToolbarTheme') || 'classic'; } catch { return 'classic'; }
  });
  const setTbTheme = (t) => {
    setToolbarTheme(t);
    try { localStorage.setItem('plannerToolbarTheme', t); } catch { /* ignore */ }
    // Avisa outros ecrãs (ex.: dashboards) para seguirem o mesmo tema ao vivo,
    // sem recarregar — o localStorage não dispara 'storage' no próprio separador.
    try { window.dispatchEvent(new CustomEvent('plannerThemeChange', { detail: t })); } catch { /* ignore */ }
  };
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [showAppDownload, setShowAppDownload] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  // Driver messages inbox — separate bell from the alert bell so message
  // traffic doesn't compete with maintenance/geofence/SLA notifications.
  // `messageInbox` is the list of drivers with at least one unread message
  // (from the dedicated /messages/unread/inbox endpoint).
  const [showMessages, setShowMessages] = useState(false);
  const [messageInbox, setMessageInbox] = useState([]);
  const [chatDriver, setChatDriver] = useState(null);
  // Detalhe do filtro de grupos activo — nomes + contagens. Carregado uma
  // vez quando o filtro muda (sem isto o chip do header só sabe o nº de IDs).
  const [groupsFilterInfo, setGroupsFilterInfo] = useState({ groups: [], deviceCount: 0 });
  const [groupsPopoverAnchor, setGroupsPopoverAnchor] = useState(null);
  const wsStatus = useSelector(selectWSStatus);
  const wsAlerts = useSelector(selectAlerts);

  // Fetch initial alerts on mount + fallback refresh every 120s (WS handles real-time)
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    function fetchAlertsFallback() {
      Promise.all([
        fetch('/planner-api/maintenance/alerts', { headers }).then(r => r.json()).catch(() => []),
        fetch('/planner-api/geofences/alerts', { headers }).then(r => r.json()).catch(() => []),
        fetch('/planner-api/stats/alerts', { headers }).then(r => r.json()).catch(() => []),
      ]).then(([maint, geo, route]) => {
        const all = [
          ...(Array.isArray(maint) ? maint : []).map(a => ({ ...a, source: 'maintenance', icon: '🔧', time: a.nextDueAt || a.createdAt })),
          ...(Array.isArray(geo) ? geo : []).slice(0, 10).map(a => ({ ...a, source: 'geofence', icon: '📍', message: `${a.vehicle?.plate || '?'} ${a.type === 'entry' ? 'entrou em' : 'saiu de'} ${a.geofence?.name || '?'}`, time: a.createdAt })),
          ...(Array.isArray(route) ? route : []).map(a => ({ ...a, source: 'route', icon: '⚠️', time: a.createdAt })),
        ];
        all.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
        setAlerts(all.slice(0, 20));
      });
    }
    fetchAlertsFallback();
    // Reduced polling — WS handles real-time, this is just for initial load + fallback
    const interval = setInterval(fetchAlertsFallback, 120000);
    return () => clearInterval(interval);
  }, [token]);

  // Driver-message inbox: initial load + live refresh on `message:new`.
  // We only count messages coming from the driver (`!fromAdmin`); operator
  // replies don't bump the badge.
  // Toast + som — quando o drawer está fechado, mostra um toast no canto
  // inferior direito (3 mais recentes) e toca um beep se o tenant tiver
  // chatNotificationSound activo.
  const [chatToasts, setChatToasts] = useState([]);
  const audioRef = useRef(null);
  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    function refresh() {
      fetch('/planner-api/messages/unread/inbox', { headers })
        .then(r => r.ok ? r.json() : [])
        .then(list => setMessageInbox(Array.isArray(list) ? list : []))
        .catch(() => setMessageInbox([]));
    }
    refresh();
    const interval = setInterval(refresh, 60000);

    // WS listener — refresh whenever a new driver→admin message lands.
    const sock = getSocket();
    function onNew(msg) {
      if (!msg || msg.fromAdmin !== false) return;
      refresh();
      // Toast só quando o drawer está fechado (estado controlado fora).
      // Usar functional setState para ler valor mais fresco sem dep.
      setChatDriver(curr => {
        if (curr) return curr; // drawer aberto — não dispara toast
        setChatToasts(prev => {
          // Substituir toast anterior do mesmo driver
          const filtered = prev.filter(t => t.driverId !== msg.driverId);
          const next = [{ id: msg.id, driverId: msg.driverId, body: msg.body, at: Date.now() }, ...filtered].slice(0, 3);
          return next;
        });
        // Som — beep curto via Web Audio API (mais fiável que data-uri).
        // Best-effort: alguns browsers exigem interacção prévia do user
        // antes de permitir audio; nesse caso falha silenciosa.
        if (clientSettings?.chatNotificationSound !== false) {
          try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (Ctx) {
              const ctx = audioRef.current || new Ctx();
              audioRef.current = ctx;
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.type = 'sine';
              osc.frequency.value = 880; // A5
              gain.gain.setValueAtTime(0.001, ctx.currentTime);
              gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
              osc.connect(gain).connect(ctx.destination);
              osc.start();
              osc.stop(ctx.currentTime + 0.25);
            }
          } catch (_) { /* ignore */ }
        }
        return curr;
      });
    }
    sock?.on('message:new', onNew);

    // SLA alert: server emite quando uma OT passa a 'critical' ou 'overdue'.
    // Dedup é feito no backend (Redis NX), aqui só adicionamos ao bell.
    function onSlaAlert(payload) {
      if (!payload?.orderId) return;
      const isOverdue = payload.status === 'overdue';
      const kindLabel = payload.kind === 'resolution'
        ? (lang === 'pt' ? 'Resolução' : 'Resolution')
        : (lang === 'pt' ? 'Resposta' : 'Response');
      const msg = isOverdue
        ? `OT ${payload.orderNumber || payload.orderId} venceu o SLA ${kindLabel}${payload.finalCustomerName ? ` (${payload.finalCustomerName})` : ''}`
        : `OT ${payload.orderNumber || payload.orderId} aproxima-se do SLA ${kindLabel} (${payload.pctElapsed}% decorrido)${payload.finalCustomerName ? ` · ${payload.finalCustomerName}` : ''}`;
      dispatch(addAlert({
        id: `sla-${payload.orderId}-${payload.kind || 'response'}-${payload.status}-${payload.firedAt}`,
        source: 'sla',
        severity: isOverdue ? 'high' : 'medium',
        message: msg,
        orderId: payload.orderId,
        time: payload.firedAt || new Date().toISOString(),
        icon: isOverdue ? '🚨' : '⏱',
      }));
      // Beep curto (mesma fonte do chat) — só se o operador não desligou
      // sons de notificação. Importante porque o SLA é tempo-crítico.
      if (clientSettings?.chatNotificationSound !== false) {
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) {
            const ctx = audioRef.current || new Ctx();
            audioRef.current = ctx;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'square';
            osc.frequency.value = isOverdue ? 1320 : 990;
            gain.gain.setValueAtTime(0.001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.20, ctx.currentTime + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.32);
            osc.connect(gain).connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.32);
          }
        } catch (_) { /* ignore */ }
      }
    }
    sock?.on('sla:alert', onSlaAlert);

    // Alerta de execução (Casais/mercadoria): OT de entrega que passou a data de
    // execução (+3 dias úteis) sem ser iniciada/terminada. Dedup no backend.
    function onCasaisExecAlert(payload) {
      if (!payload?.orderId) return;
      const notStarted = payload.kind === 'not-started';
      const what = notStarted
        ? (lang === 'pt' ? 'não foi iniciada' : 'not started')
        : (lang === 'pt' ? 'não foi terminada' : 'not finished');
      const obraTxt = payload.obra ? ` · Obra ${payload.obra}` : '';
      const msg = lang === 'pt'
        ? `OT ${payload.orderNumber || payload.orderId} ${what} no prazo (3 dias úteis)${obraTxt}`
        : `OT ${payload.orderNumber || payload.orderId} ${what} within the window${obraTxt}`;
      dispatch(addAlert({
        id: `casais-exec-${payload.orderId}-${payload.kind}`,
        source: 'casais-exec',
        severity: 'high',
        message: msg,
        orderId: payload.orderId,
        time: payload.firedAt || new Date().toISOString(),
        icon: '⚠️',
      }));
    }
    sock?.on('casais:execution-alert', onCasaisExecAlert);

    return () => {
      clearInterval(interval);
      sock?.off('message:new', onNew);
      sock?.off('sla:alert', onSlaAlert);
      sock?.off('casais:execution-alert', onCasaisExecAlert);
    };
  }, [token, clientSettings?.chatNotificationSound, dispatch]);

  // Auto-dismiss dos toasts após 8s
  useEffect(() => {
    if (chatToasts.length === 0) return;
    const t = setTimeout(() => {
      setChatToasts(prev => prev.slice(0, -1));
    }, 8000);
    return () => clearTimeout(t);
  }, [chatToasts]);

  const totalUnreadMessages = messageInbox.reduce((acc, e) => acc + (e.unreadCount || 0), 0);

  // Carrega nomes + contagens dos grupos filtrados sempre que a lista de
  // visibleGroupIds muda. Sem isto o chip só conhece os IDs e não dá para
  // mostrar nomes/contagens ao operador.
  const visibleGroupIdsKey = (clientSettings?.visibleGroupIds || []).join(',');
  useEffect(() => {
    if (!token) return;
    const ids = clientSettings?.visibleGroupIds || [];
    if (!Array.isArray(ids) || ids.length === 0) {
      setGroupsFilterInfo({ groups: [], deviceCount: 0 });
      return;
    }
    let cancelled = false;
    fetch('/planner-api/vehicles/groups', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const idSet = new Set(ids.map(Number));
        const selected = (data.groups || []).filter(g => idSet.has(Number(g.id)));
        const totalDevices = selected.reduce((s, g) => s + (g.deviceCount || 0), 0);
        setGroupsFilterInfo({ groups: selected, deviceCount: totalDevices });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, visibleGroupIdsKey]);

  function openChatWith(entry) {
    setChatDriver({
      driverId: entry.driverId,
      driverName: entry.driverName,
      driverPhone: entry.driverPhone,
      activeOT: entry.activeOT,
    });
    setShowMessages(false);
  }
  function closeChat() {
    setChatDriver(null);
    // Refresh the inbox so the just-read driver drops off the list.
    if (token) {
      fetch('/planner-api/messages/unread/inbox', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(list => setMessageInbox(Array.isArray(list) ? list : []))
        .catch(() => {});
    }
  }

  // Merge WS real-time alerts with REST alerts
  const mergedAlerts = React.useMemo(() => {
    const all = [...wsAlerts, ...alerts];
    // Deduplicate by id or message+time
    const seen = new Set();
    return all.filter(a => {
      const key = a.id || `${a.message}-${a.time}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0)).slice(0, 30);
  }, [wsAlerts, alerts]);

  // Cores dos chips informativos do header (chegada / janela de entrega /
  // filtro de grupos). Os valores originais foram desenhados para fundo
  // ESCURO (texto claro); no tema "claro" esse texto desaparecia sobre a
  // barra clara. Aqui resolvemos por tema para manter o contraste.
  const isLightTb = toolbarTheme === 'light';
  const infoChip = isLightTb
    ? { bg: 'rgba(7,150,204,0.10)', border: 'rgba(7,150,204,0.32)', fg: '#0677a6', fgDim: '#3a4e66' }
    : { bg: 'rgba(255,255,255,0.15)', border: 'rgba(255,255,255,0.25)', fg: '#ffffff', fgDim: 'rgba(255,255,255,0.7)' };
  const deliveryChip = isLightTb
    ? { bg: 'rgba(217,119,6,0.14)', border: 'rgba(217,119,6,0.45)', fg: '#b45309' }
    : { bg: 'rgba(245,158,11,0.2)', border: 'rgba(245,158,11,0.4)', fg: '#fde68a' };
  const groupChip = isLightTb
    ? { bg: 'rgba(202,138,4,0.14)', bgHover: 'rgba(202,138,4,0.22)', border: 'rgba(202,138,4,0.5)', fg: '#92400e', divider: 'rgba(146,64,14,0.3)' }
    : { bg: 'rgba(254,240,138,0.22)', bgHover: 'rgba(254,240,138,0.36)', border: 'rgba(253,224,71,0.55)', fg: '#fef3c7', divider: 'rgba(254,243,199,0.4)' };
  // Menu do utilizador (popover do avatar): segue o tema — Clássico/Claro = claro,
  // Escuro = superfícies escuras. O cartão de identidade (gradiente) fica igual.
  const isDarkTb = toolbarTheme === 'dark';
  const menu = isDarkTb
    ? { paper: '#122133', shadow: '0 20px 48px rgba(0,0,0,0.6)', paperBorder: '1px solid rgba(110,193,228,0.14)',
        text: '#eaf3fb', textMute: '#8ba3bb', hover: 'rgba(255,255,255,0.06)', divider: 'rgba(255,255,255,0.08)',
        iconBg: 'rgba(99,102,241,0.22)', cardActiveBg: 'rgba(79,70,229,0.28)', cardActiveBorder: 'rgba(129,140,248,0.55)', cardBorder: 'rgba(255,255,255,0.12)',
        accent: '#a5b4fc', logoutHover: 'rgba(220,38,38,0.14)', logoutIconBg: 'rgba(220,38,38,0.22)', logoutText: '#fca5a5' }
    : { paper: '#ffffff', shadow: '0 20px 48px rgba(15,23,42,0.18)', paperBorder: 'none',
        text: '#0f172a', textMute: '#94a3b8', hover: '#f8fafc', divider: '#f1f5f9',
        iconBg: '#eef2ff', cardActiveBg: '#eef2ff', cardActiveBorder: '#c7d2fe', cardBorder: '#e2e8f0',
        accent: '#4f46e5', logoutHover: '#fef2f2', logoutIconBg: '#fee2e2', logoutText: '#991b1b' };

  return (
    <Box className={classes.header} data-tb-theme={toolbarTheme}>

      {/* Symbol logo + discreet client badge (short tag on hover shows full name) */}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 14, flexShrink: 0 }}>
        {/* Logótipo ecooGPS REAL, transparente (sem a caixa navy do symbol.png):
            versão de texto ESCURO para o tema Claro e de texto CLARO para
            Escuro/Clássico. Fundo transparente → coincide sempre com o tema. */}
        <img
          src={toolbarTheme === 'light' ? '/ecoogps-logo-onlight.png' : '/ecoogps-logo-ondark.png'}
          alt="ecooGPS"
          style={{ height: 30, width: 'auto', display: 'block' }}
        />
        {clientName && (() => {
          // Derive a short tag from either the email suffix (jpmkblack+transnil → "transnil")
          // or the last word of the display name ("João Paulo Transnil" → "Transnil").
          const emailLike = userEmail || '';
          const plusMatch = emailLike.match(/\+([^@]+)@/);
          const shortTag = plusMatch
            ? plusMatch[1]
            : (clientName.trim().split(/\s+/).pop() || clientName);
          return (
            <Tooltip title={clientName} placement="bottom">
              <Box style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '3px 8px', borderRadius: 14,
                background: 'var(--tb-chip-bg, rgba(99,102,241,0.08))',
                border: '1px solid var(--tb-chip-border, rgba(99,102,241,0.2))',
                cursor: 'default', maxWidth: 140,
              }}>
                <Box style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--tb-fg-dim, #6366f1)', flexShrink: 0 }} />
                <Typography style={{
                  fontSize: '0.62rem', fontWeight: 700, color: 'var(--tb-fg-dim, #6366f1)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  textTransform: 'lowercase', letterSpacing: '0.02em',
                }}>
                  {shortTag}
                </Typography>
              </Box>
            </Tooltip>
          );
        })()}
        {isMoveExpressTenant(clientExternalId, clientSettings) && (
          <Tooltip title={lang === 'pt' ? 'Ajuda — guia do operador' : 'Help — operator guide'} placement="bottom">
            <IconButton
              size="small"
              onClick={() => window.dispatchEvent(new Event('openHelpCenter'))}
              style={{ padding: 4, color: '#15803d' }}
            >
              <HelpOutlineIcon style={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        )}
        {clientName && canEditPrefs && (
          <Tooltip title={lang === 'pt' ? 'Preferências' : 'Preferences'} placement="bottom">
            <IconButton
              size="small"
              onClick={() => setShowPrefs(true)}
              style={{ padding: 4, color: '#64748b' }}
            >
              <SettingsIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <PreferencesDialog open={showPrefs} onClose={() => setShowPrefs(false)} />

      {/* Navigation Tabs — cada botão é um MÓDULO (modelo "menu = módulo").
          `navTabs` já vem filtrado pelos módulos do Perfil. */}
      <Box className={classes.navTabs}>
        {navTabs.map(tab => (
          <Box
            key={tab.id}
            className={`${classes.navTab} ${activeView === tab.id ? classes.navTabActive : ''}`}
            onClick={() => {
              dispatch(setActiveView(tab.id));
              // When clicking the Planner tab: clear OT-screen flags but
              // KEEP the sidebar visible if a route is already loaded —
              // operators expect to "go back" to the planner and see the
              // route they were working on, with its sidebar tab. Only
              // hide the sidebar (clean planner) when there's no route in
              // memory.
              if (tab.id === 'planner') {
                const hasLoadedRoute = !!(route?.computedRoute || route?.dbId);
                if (!hasLoadedRoute) {
                  dispatch(setSidebarVisible(false));
                }
                window.__fromOTScreen = false;
                window.__fromDriversScreen = false;
                window.__inspectingRoute = false;
                window.__monitoringOT = null;
                window.dispatchEvent(new CustomEvent('focusSingleVehicle', { detail: { id: null } }));
                if (!hasLoadedRoute) {
                  window.dispatchEvent(new CustomEvent('resetMapView'));
                }
              }
            }}
          >
            <tab.icon style={{ fontSize: 15 }} />
            {tab.label}
          </Box>
        ))}
      </Box>

      {/* Arrival time indicator — uses tachograph-aware ETA */}
      {route.computedRoute && route.departureTime && (() => {
        const wpStops = (route.waypoints || []).filter(wp => wp.stopDuration > 0).map(wp => ({ duration: wp.stopDuration }));
        const etaResult = calculateETA(route.departureTime, route.computedRoute.duration, route.stops || [], wpStops);
        if (!etaResult) return null;
        const { eta, totalDriveMin, totalStopMin } = etaResult;
        const now = new Date();
        const dayDiff = Math.floor((eta - now) / 86400000);
        const dayLabel = dayDiff === 0
          ? (lang === 'pt' ? 'Hoje' : 'Today')
          : dayDiff === 1
            ? (lang === 'pt' ? 'Amanhã' : 'Tomorrow')
            : eta.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
        const timeLabel = eta.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
        const daysTravel = Math.ceil((totalDriveMin + totalStopMin) / (24 * 60));
        return (
          <Tooltip title={
            `${lang === 'pt' ? 'Condução' : 'Driving'}: ${Math.floor(totalDriveMin / 60)}h${totalDriveMin % 60 > 0 ? (totalDriveMin % 60) + 'min' : ''} | ` +
            `${lang === 'pt' ? 'Paragens' : 'Stops'}: ${Math.floor(totalStopMin / 60)}h${totalStopMin % 60 > 0 ? (totalStopMin % 60) + 'min' : ''}` +
            (daysTravel > 1 ? ` | ${daysTravel} ${lang === 'pt' ? 'dias' : 'days'}` : '')
          }>
            <Box style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px',
              background: infoChip.bg, border: `1px solid ${infoChip.border}`,
              borderRadius: 20, marginLeft: 12, flexShrink: 0,
            }}>
              <ScheduleIcon style={{ fontSize: 14, color: infoChip.fg }} />
              <Typography style={{ fontSize: '0.7rem', fontWeight: 600, color: infoChip.fg }}>
                {lang === 'pt' ? 'Chegada' : 'Arrival'}: {dayLabel} {timeLabel}
              </Typography>
              {daysTravel > 1 && (
                <Typography style={{ fontSize: '0.58rem', color: infoChip.fgDim, marginLeft: 2 }}>
                  ({daysTravel}d)
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })()}

      {/* Delivery time window from OT */}
      {route.arrivalTimeWindow?.start && (() => {
        const winStart = new Date(route.arrivalTimeWindow.start);
        const winLabel = winStart.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
        const winDate = winStart.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
        return (
          <Box style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
            background: deliveryChip.bg, border: `1px solid ${deliveryChip.border}`,
            borderRadius: 20, marginLeft: 6, flexShrink: 0,
          }}>
            <Typography style={{ fontSize: '0.6rem', fontWeight: 600, color: deliveryChip.fg }}>
              {lang === 'pt' ? 'Entrega' : 'Delivery'}: {winDate} {winLabel}
            </Typography>
          </Box>
        );
      })()}

      {/* Filtro de grupos activo — chip com nº de grupos e viaturas.
          Click abre popover com a lista de grupos e atalho para gerir. */}
      {Array.isArray(clientSettings?.visibleGroupIds) && clientSettings.visibleGroupIds.length > 0 && (() => {
        const nGroups = clientSettings.visibleGroupIds.length;
        const nVehicles = groupsFilterInfo.deviceCount;
        return (
          <>
            <Box
              onClick={e => setGroupsPopoverAnchor(e.currentTarget)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '4px 11px',
                background: groupChip.bg,
                border: `1px solid ${groupChip.border}`,
                borderRadius: 20, marginLeft: 8, flexShrink: 0, cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = groupChip.bgHover; }}
              onMouseLeave={e => { e.currentTarget.style.background = groupChip.bg; }}
            >
              <FilterListIcon style={{ fontSize: 13, color: groupChip.fg }} />
              <Typography style={{ fontSize: '0.66rem', fontWeight: 700, color: groupChip.fg, letterSpacing: 0.3 }}>
                {nGroups} {nGroups === 1
                  ? (lang === 'pt' ? 'grupo' : 'group')
                  : (lang === 'pt' ? 'grupos' : 'groups')}
              </Typography>
              <Box style={{ width: 1, height: 12, background: groupChip.divider }} />
              <Typography style={{ fontSize: '0.66rem', fontWeight: 600, color: groupChip.fg, letterSpacing: 0.2 }}>
                {nVehicles} {nVehicles === 1
                  ? (lang === 'pt' ? 'viatura' : 'vehicle')
                  : (lang === 'pt' ? 'viaturas' : 'vehicles')}
              </Typography>
            </Box>
            <Popover
              open={Boolean(groupsPopoverAnchor)}
              anchorEl={groupsPopoverAnchor}
              onClose={() => setGroupsPopoverAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              PaperProps={{
                style: {
                  marginTop: 6, minWidth: 280, maxWidth: 360,
                  borderRadius: 14, overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(28,28,28,0.18)',
                },
              }}
            >
              {/* Header do popover */}
              <Box style={{
                padding: '14px 18px 10px',
                background: 'linear-gradient(135deg, #f5f5f4 0%, #fef9c3 60%, #fde68a 100%)',
                borderBottom: '1px solid #e7e5e4',
              }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <Box style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: 'rgba(202,138,4,0.18)',
                    border: '1px solid rgba(202,138,4,0.32)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <FilterListIcon style={{ fontSize: 16, color: '#a16207' }} />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1c1917', letterSpacing: '-0.01em' }}>
                      {lang === 'pt' ? 'Filtro de grupos activo' : 'Active group filter'}
                    </Typography>
                    <Typography style={{ fontSize: '0.66rem', color: '#78716c', marginTop: 1 }}>
                      {nGroups} {nGroups === 1 ? (lang === 'pt' ? 'grupo' : 'group') : (lang === 'pt' ? 'grupos' : 'groups')}
                      {' · '}
                      {nVehicles} {nVehicles === 1 ? (lang === 'pt' ? 'viatura' : 'vehicle') : (lang === 'pt' ? 'viaturas' : 'vehicles')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Lista de grupos */}
              <Box style={{ maxHeight: 280, overflowY: 'auto', padding: '6px 0' }}>
                {groupsFilterInfo.groups.length === 0 ? (
                  <Typography style={{ fontSize: '0.74rem', color: '#a8a29e', padding: '14px 18px', textAlign: 'center' }}>
                    {lang === 'pt' ? 'A carregar grupos…' : 'Loading groups…'}
                  </Typography>
                ) : groupsFilterInfo.groups.map(g => (
                  <Box key={g.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 18px',
                  }}>
                    <Box style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#ca8a04', flexShrink: 0,
                    }} />
                    <Typography style={{
                      flex: 1, fontSize: '0.78rem', fontWeight: 600, color: '#292524',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {g.name}
                    </Typography>
                    <Box style={{
                      padding: '2px 8px', borderRadius: 10,
                      background: '#fef3c7', color: '#92400e',
                      fontSize: '0.66rem', fontWeight: 700,
                    }}>
                      {g.deviceCount}
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Footer */}
              <Box style={{
                padding: '10px 16px', borderTop: '1px solid #e7e5e4',
                background: '#fafaf9',
                display: 'flex', gap: 8, justifyContent: 'flex-end',
              }}>
                <Button
                  size="small"
                  onClick={() => {
                    setGroupsPopoverAnchor(null);
                    dispatch(setActiveView('fleet'));
                    setTimeout(() => window.dispatchEvent(new CustomEvent('openFleetGroupsDialog')), 80);
                  }}
                  style={{
                    textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
                    background: '#ca8a04', color: '#fff',
                    padding: '4px 14px', borderRadius: 8,
                    boxShadow: '0 2px 6px rgba(202,138,4,0.28)',
                  }}
                >
                  {lang === 'pt' ? 'Gerir filtro' : 'Manage filter'}
                </Button>
              </Box>
            </Popover>
          </>
        );
      })()}

      {/* Spacer */}
      <Box style={{ flex: 1 }} />

      {/* Actions */}
      <Box className={classes.actions}>

        {/* Overflow menu (...) — só tem entradas da App móvel (descarregar +
            QR Code). Sem App no fluxo das OT, não serve para nada. */}
        {usesDriverApp && (
        <Tooltip title={lang === 'pt' ? 'Mais opções' : 'More options'}>
          <IconButton
            size="small"
            className={classes.actionIconBtn}
            onClick={e => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon style={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        )}

        <Popover
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: classes.popoverPaper }}
        >
          <Box className={classes.menuList}>
            {/* Download APK — direct. ?v=… força browsers (e o Package
                Installer Android) a não reutilizar uma APK antiga já em
                Downloads — sem isto, fizemos hard refresh e mesmo assim
                o telemóvel servia uma versão cached. */}
            <Box
              className={classes.menuItem}
              onClick={async () => {
                setMenuAnchor(null);
                let v = '';
                try {
                  const r = await fetch(`/mobile/version.json?t=${Date.now()}`, { cache: 'no-store' });
                  if (r.ok) { const d = await r.json(); v = d?.version || ''; }
                } catch { /* ignore */ }
                const link = document.createElement('a');
                link.href = v ? `/mobile/ecoogps-mobile.apk?v=${encodeURIComponent(v)}` : '/mobile/ecoogps-mobile.apk';
                link.download = 'EcooGPS-Mobile.apk';
                link.click();
              }}
            >
              <Box className={classes.menuItemIcon} style={{ background: 'rgba(34,197,94,0.10)' }}>
                <GetAppIcon style={{ fontSize: 18, color: '#22c55e' }} />
              </Box>
              <Box className={classes.menuItemText}>
                <Typography className={classes.menuItemTitle}>
                  {lang === 'pt' ? 'App Móvel — Descarregar' : 'Mobile App — Download'}
                </Typography>
                <Typography className={classes.menuItemDesc}>
                  {lang === 'pt' ? 'Descarregar APK para Android' : 'Download APK for Android'}
                </Typography>
              </Box>
              <span className={classes.menuItemBadge} style={{ background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>
                APK
              </span>
            </Box>

            {/* Download via QR Code */}
            <Box
              className={classes.menuItem}
              onClick={() => { setMenuAnchor(null); setShowAppDownload(true); }}
            >
              <Box className={classes.menuItemIcon} style={{ background: 'rgba(245,158,11,0.10)' }}>
                <SmartphoneIcon style={{ fontSize: 18, color: '#f59e0b' }} />
              </Box>
              <Box className={classes.menuItemText}>
                <Typography className={classes.menuItemTitle}>
                  {lang === 'pt' ? 'App Móvel — QR Code' : 'Mobile App — QR Code'}
                </Typography>
                <Typography className={classes.menuItemDesc}>
                  {lang === 'pt' ? 'Ler QR Code com o telemóvel' : 'Scan QR Code with phone'}
                </Typography>
              </Box>
            </Box>

          </Box>
        </Popover>

        {/* WebSocket status indicator */}
        <Tooltip title={wsStatus.connected ? `WebSocket activo (${wsStatus.id || ''})` : 'WebSocket desligado — a usar polling'}>
          <Box style={{
            width: 8, height: 8, borderRadius: '50%',
            background: wsStatus.connected ? '#22c55e' : '#ef4444',
            boxShadow: wsStatus.connected ? '0 0 6px rgba(34,197,94,0.5)' : '0 0 6px rgba(239,68,68,0.5)',
            marginRight: 4, flexShrink: 0,
          }} />
        </Tooltip>

        {/* Driver messages bell — separate from alerts so chat traffic stays
            visually distinct from maintenance/geofence/SLA notifications.
            Sem App no fluxo das OT não há motoristas com quem falar. */}
        {usesDriverApp && (
        <Box style={{ position: 'relative' }}>
          <Tooltip title={lang === 'pt' ? 'Mensagens motoristas' : 'Driver messages'}>
            <IconButton size="small" className={classes.actionIconBtn}
              onClick={() => setShowMessages(!showMessages)}>
              <ChatBubbleOutlineIcon style={{ fontSize: 17 }} />
              {totalUnreadMessages > 0 && (
                <Box style={{
                  position: 'absolute', top: 2, right: 2, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7,
                  background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{totalUnreadMessages > 9 ? '9+' : totalUnreadMessages}</span>
                </Box>
              )}
            </IconButton>
          </Tooltip>
          {showMessages && (
            <>
              <Box style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowMessages(false)} />
              <Box style={{
                position: 'absolute', top: 38, right: 0, width: 340, maxHeight: 420,
                background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: '1px solid #e2e8f0', overflow: 'hidden', zIndex: 1000,
                display: 'flex', flexDirection: 'column',
              }}>
                <Box style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ChatBubbleOutlineIcon style={{ fontSize: 16, color: '#2563eb' }} />
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', flex: 1 }}>
                    {lang === 'pt' ? 'Mensagens dos motoristas' : 'Driver messages'}
                  </Typography>
                  {totalUnreadMessages > 0 && (
                    <Chip label={totalUnreadMessages} size="small" style={{ height: 18, fontSize: '0.55rem', fontWeight: 700, background: '#dbeafe', color: '#1e40af' }} />
                  )}
                  <Tooltip title={lang === 'pt' ? 'Abrir chat completo' : 'Open full chat'}>
                    <Box
                      onClick={() => { setShowMessages(false); setChatDriver({ driverId: null }); }}
                      style={{ fontSize: '0.66rem', fontWeight: 700, color: '#2563eb', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, background: '#eff6ff' }}
                    >
                      {lang === 'pt' ? 'Abrir' : 'Open'} →
                    </Box>
                  </Tooltip>
                </Box>
                <Box style={{ overflowY: 'auto', flex: 1 }}>
                  {messageInbox.length === 0 ? (
                    <Box style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem' }}>
                      {lang === 'pt' ? 'Sem mensagens novas' : 'No new messages'}
                    </Box>
                  ) : messageInbox.map(entry => {
                    const ot = entry.activeOT;
                    const ago = entry.lastMessage?.createdAt
                      ? Math.max(0, Math.floor((Date.now() - new Date(entry.lastMessage.createdAt).getTime()) / 60000))
                      : null;
                    return (
                      <Box key={entry.driverId}
                        onClick={() => openChatWith(entry)}
                        style={{
                          padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', gap: 4,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.driverName}
                          </Typography>
                          {entry.unreadCount > 1 && (
                            <Chip label={entry.unreadCount} size="small" style={{ height: 16, fontSize: '0.55rem', fontWeight: 700, background: '#dbeafe', color: '#1e40af' }} />
                          )}
                          {ago != null && (
                            <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                              {ago === 0 ? 'agora' : ago < 60 ? `há ${ago}m` : `há ${Math.floor(ago / 60)}h`}
                            </Typography>
                          )}
                        </Box>
                        <Typography style={{ fontSize: '0.7rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entry.lastMessage?.body || '—'}
                        </Typography>
                        {ot && (
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <AssignmentIcon style={{ fontSize: 11, color: '#64748b' }} />
                            <Typography style={{ fontSize: '0.6rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ot.orderNumber || `OT #${ot.id}`} · {ot.deliveryName || ot.deliveryAddress || 'em curso'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
        </Box>
        )}

        {/* Notifications bell — SEMPRE visível: os alertas (manutenção,
            geofence, rota, WS) vêm do GPS do veículo, não da App. */}
        <Box style={{ position: 'relative' }}>
          <Tooltip title={lang === 'pt' ? 'Alertas' : 'Alerts'}>
            <IconButton size="small" className={classes.actionIconBtn}
              onClick={() => setShowAlerts(!showAlerts)}>
              <NotificationsIcon style={{ fontSize: 17 }} />
              {mergedAlerts.length > 0 && (
                <Box style={{
                  position: 'absolute', top: 2, right: 2, width: 14, height: 14, borderRadius: '50%',
                  background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{mergedAlerts.length > 9 ? '9+' : mergedAlerts.length}</span>
                </Box>
              )}
            </IconButton>
          </Tooltip>
          {showAlerts && (
            <>
              <Box style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowAlerts(false)} />
              <Box style={{
                position: 'absolute', top: 38, right: 0, width: 340, maxHeight: 380,
                background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: '1px solid #e2e8f0', overflow: 'hidden', zIndex: 1000,
              }}>
                <Box style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                    {lang === 'pt' ? 'Alertas' : 'Alerts'}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Chip label={mergedAlerts.length} size="small" style={{ height: 18, fontSize: '0.55rem', fontWeight: 700, background: '#fee2e2', color: '#ef4444' }} />
                    {wsStatus.connected && (
                      <Chip label="LIVE" size="small" style={{ height: 16, fontSize: '0.45rem', fontWeight: 800, background: '#dcfce7', color: '#16a34a', letterSpacing: '0.05em' }} />
                    )}
                  </Box>
                </Box>
                <Box style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {mergedAlerts.length === 0 ? (
                    <Typography style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: '0.78rem' }}>
                      {lang === 'pt' ? 'Sem alertas' : 'No alerts'}
                    </Typography>
                  ) : mergedAlerts.map((a, i) => {
                    const isIncident = a.source === 'incident';
                    return (
                      <Box key={a.id || i} style={{
                        display: 'flex', gap: 8, padding: '8px 14px', borderBottom: '1px solid #f8fafc',
                        background: isIncident ? '#fef2f2' : a.source === 'realtime' ? '#f0fdf4' : i % 2 === 0 ? '#fff' : '#fafbfc',
                        borderLeft: isIncident ? '3px solid #dc2626' : 'none',
                      }}>
                        <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{a.icon}</span>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Typography style={{ fontSize: '0.72rem', fontWeight: isIncident ? 800 : 600, color: isIncident ? '#991b1b' : '#1e293b' }}>
                            {a.message || a.typeLabel || a.description || '—'}
                          </Typography>
                          {a.detail && (
                            <Typography style={{ fontSize: '0.66rem', color: '#475569', marginTop: 1 }}>
                              {a.detail}
                            </Typography>
                          )}
                          <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                            {a.source === 'maintenance' ? '🔧 Manutenção' : a.source === 'geofence' ? '📍 Geofence' : a.source === 'route' ? '🚛 Rota' : a.source === 'realtime' ? '⚡ Tempo real' : a.source === 'incident' ? '🚨 Incidente' : a.source === 'sla' ? '⏱ SLA' : '⚠️ Alerta'}
                            {a.time ? ` · ${new Date(a.time).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                          </Typography>
                          {isIncident && a.routeId && (
                            <Box style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                              <Button size="small" variant="outlined"
                                onClick={() => {
                                  setShowAlerts(false);
                                  dispatch(setActiveView('orders'));
                                }}
                                style={{ fontSize: '0.62rem', textTransform: 'none', padding: '2px 8px', minHeight: 22, color: '#dc2626', borderColor: '#fecaca' }}>
                                Ver OTs libertadas
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box className={classes.rightDivider} />

        <Tooltip title={lang === 'pt' ? 'Switch to English' : 'Mudar para Português'}>
          <IconButton
            size="small"
            className={classes.langBtn}
            onClick={() => dispatch(setLang(lang === 'pt' ? 'en' : 'pt'))}
          >
            <LanguageIcon style={{ fontSize: 17 }} />
            <Typography style={{ fontSize: '0.60rem', marginLeft: 2, fontWeight: 700, color: 'inherit' }}>
              {lang.toUpperCase()}
            </Typography>
          </IconButton>
        </Tooltip>

        <Tooltip title={t(lang, 'headerFullscreen')}>
          <IconButton
            size="small"
            className={classes.actionIconBtn}
            onClick={() => {
              if (!document.fullscreenElement) document.documentElement.requestFullscreen();
              else document.exitFullscreen();
            }}
          >
            <FullscreenIcon style={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>

        {/* User avatar menu — initials button that opens a soft popover with
            user info + logout action. Replaces the earlier raw icon button. */}
        {(() => {
          const initials = (() => {
            const src = (userEmail || clientName || 'U').replace(/\+.*@/, '@');
            const before = src.split('@')[0];
            const parts = before.replace(/[._-]/g, ' ').split(/\s+/).filter(Boolean);
            if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
            return (parts[0] || 'U').slice(0, 2).toUpperCase();
          })();
          return (
            <>
              <Tooltip title={userEmail || (lang === 'pt' ? 'Conta' : 'Account')} placement="bottom">
                <Box
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '2px 6px 2px 4px', borderRadius: 16,
                    background: userMenuAnchor ? 'var(--tb-hover-bg, rgba(255,255,255,0.1))' : 'transparent',
                    border: '1px solid var(--tb-chip-border, rgba(255,255,255,0.1))',
                    cursor: 'pointer', transition: 'background 0.15s',
                    marginLeft: 2,
                  }}
                >
                  <Box style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'var(--tb-active-bg, rgba(255,255,255,0.14))',
                    border: '1px solid var(--tb-chip-border, rgba(255,255,255,0.24))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--tb-fg, #fff)', fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.02em',
                  }}>
                    {initials}
                  </Box>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="var(--tb-fg, #fff)" style={{ opacity: 0.55 }}>
                    <path d="M7 10l5 5 5-5z"/>
                  </svg>
                </Box>
              </Tooltip>
              <Popover
                open={Boolean(userMenuAnchor)}
                anchorEl={userMenuAnchor}
                onClose={() => setUserMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  style: {
                    width: 280, borderRadius: 14, marginTop: 8, overflow: 'hidden',
                    background: menu.paper, border: menu.paperBorder,
                    boxShadow: menu.shadow,
                  },
                }}
              >
                {/* Identity card */}
                <Box style={{
                  padding: '18px 18px 14px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff',
                }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Box style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.22)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.92rem', fontWeight: 800, letterSpacing: '0.04em',
                      border: '2px solid rgba(255,255,255,0.3)',
                    }}>
                      {initials}
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{
                        fontSize: '0.84rem', fontWeight: 800, color: '#fff',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {clientName || (lang === 'pt' ? 'Utilizador' : 'User')}
                      </Typography>
                      <Typography style={{
                        fontSize: '0.66rem', color: 'rgba(255,255,255,0.85)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {userEmail || '—'}
                      </Typography>
                    </Box>
                  </Box>
                  {userRole && (
                    <Box style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      marginTop: 10, padding: '2px 8px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.18)',
                      fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
                    }}>
                      <Box style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80' }} />
                      {userRole}
                    </Box>
                  )}
                </Box>

                {/* Actions */}
                <Box style={{ padding: 6 }}>
                  {/* Preferências — só GESTOR (permissionLevel) ou super-admin.
                      Escrevem no Perfil da Organização, por isso viewer (só
                      leitura) e operator (cria OTs/rotas/favoritos) não as vêem. */}
                  {/* Administração (org_admin) — movida da barra para aqui. */}
                  {Array.isArray(modules) && modules.includes('org_admin') && (<>
                  <Box
                    onClick={() => {
                      setUserMenuAnchor(null);
                      dispatch(setActiveView('org_admin'));
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 8,
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = menu.hover}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Box style={{
                      width: 30, height: 30, borderRadius: 8, background: menu.iconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <SupervisorAccountIcon style={{ fontSize: 15, color: '#6366f1' }} />
                    </Box>
                    <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: menu.text }}>
                      {lang === 'pt' ? 'Administração' : 'Administration'}
                    </Typography>
                  </Box>
                  <Box style={{ height: 1, background: menu.divider, margin: '4px 8px' }} />
                  </>)}

                  {canEditPrefs && (<>
                  <Box
                    onClick={() => {
                      setUserMenuAnchor(null);
                      setShowPrefs(true);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 8,
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = menu.hover}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Box style={{
                      width: 30, height: 30, borderRadius: 8, background: menu.iconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <SettingsIcon style={{ fontSize: 15, color: '#6366f1' }} />
                    </Box>
                    <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: menu.text }}>
                      {lang === 'pt' ? 'Preferências' : 'Preferences'}
                    </Typography>
                  </Box>

                  <Box style={{ height: 1, background: menu.divider, margin: '4px 8px' }} />
                  </>)}

                  {/* Tema da barra principal: Clássico (atual) / Claro / Escuro. */}
                  <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: menu.textMute, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 12px 2px' }}>
                    {lang === 'pt' ? 'Tema da barra' : 'Toolbar theme'}
                  </Typography>
                  <Box style={{ display: 'flex', gap: 6, padding: '2px 10px 6px' }}>
                    {[
                      { k: 'classic', pt: 'Clássico', en: 'Classic', sw: 'linear-gradient(90deg,#1a5a72,#27ae78)' },
                      { k: 'light', pt: 'Claro', en: 'Light', sw: '#eef5fb' },
                      { k: 'dark', pt: 'Escuro', en: 'Dark', sw: 'linear-gradient(90deg,#0a1726,#0e2235)' },
                    ].map(opt => {
                      const active = toolbarTheme === opt.k;
                      return (
                        <Box key={opt.k} onClick={() => setTbTheme(opt.k)}
                          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '7px 4px', borderRadius: 8, cursor: 'pointer',
                            background: active ? menu.cardActiveBg : 'transparent', border: `1px solid ${active ? menu.cardActiveBorder : menu.cardBorder}` }}>
                          <Box style={{ width: '100%', height: 18, borderRadius: 5, background: opt.sw, border: '1px solid rgba(0,0,0,0.06)' }} />
                          <Typography style={{ fontSize: '0.64rem', fontWeight: active ? 800 : 600, color: active ? menu.accent : menu.textMute }}>
                            {lang === 'pt' ? opt.pt : opt.en}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box style={{ height: 1, background: menu.divider, margin: '4px 8px' }} />
                  <Typography style={{ fontSize: '0.62rem', color: menu.textMute, padding: '2px 12px 6px', textAlign: 'center' }}>
                    EcooGPS Planner · v{version}
                  </Typography>

                  <Box
                    onClick={() => {
                      setUserMenuAnchor(null);
                      if (!window.confirm(lang === 'pt' ? 'Queres terminar a sessão?' : 'Sign out?')) return;
                      try { sessionStorage.removeItem('planner_token'); } catch {}
                      try { localStorage.removeItem('planner_token'); } catch {}
                      document.cookie = 'JSESSIONID=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
                      window.location.href = '/';
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 8,
                      cursor: 'pointer', transition: 'all 0.12s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = menu.logoutHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Box style={{
                      width: 30, height: 30, borderRadius: 8, background: menu.logoutIconBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <ExitToAppIcon style={{ fontSize: 15, color: '#dc2626' }} />
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: menu.logoutText }}>
                        {lang === 'pt' ? 'Terminar sessão' : 'Sign out'}
                      </Typography>
                      <Typography style={{ fontSize: '0.64rem', color: menu.textMute }}>
                        {lang === 'pt' ? 'Voltar ao ecrã de login' : 'Back to login screen'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Popover>
            </>
          );
        })()}

      </Box>
      <ManageDialog
        open={showManageDialog}
        onClose={() => setShowManageDialog(false)}
        lang={lang}
        onShowRoutesOnMap={(routes) => {
          if (routes.length === 1) {
            // Single route: fetch full detail and load as current route
            dispatch(setDisplayedSavedRoutes([]));
            if (routes[0].dbId && token) {
              dispatch(fetchRouteDetail({ token, routeId: routes[0].dbId }));
            } else {
              dispatch(loadRoute(routes[0].id));
            }
            dispatch(setShowDetails(true));
            dispatch(setSidebarTab(0));
          } else {
            // Multiple routes: overlay on map
            dispatch(setDisplayedSavedRoutes(routes));
          }
          setShowManageDialog(false);
        }}
      />
      <AppDownloadModal
        open={showAppDownload}
        onClose={() => setShowAppDownload(false)}
        lang={lang}
      />
      <ChatDrawer
        open={!!chatDriver}
        onClose={closeChat}
        initialDriverId={chatDriver?.driverId}
        initialDriverName={chatDriver?.driverName}
        initialDriverPhone={chatDriver?.driverPhone}
        initialActiveOT={chatDriver?.activeOT}
      />

      {/* Toast stack para mensagens novas — só aparece quando o drawer
          está fechado. Click abre o drawer naquele driver. */}
      {chatToasts.length > 0 && !chatDriver && (
        <Box style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1280, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {chatToasts.map(t => {
            const entry = messageInbox.find(e => e.driverId === t.driverId);
            return (
              <Box
                key={t.id}
                onClick={() => {
                  setChatToasts(prev => prev.filter(x => x.id !== t.id));
                  setChatDriver({
                    driverId: t.driverId,
                    driverName: entry?.driverName,
                    driverPhone: entry?.driverPhone,
                    activeOT: entry?.activeOT,
                  });
                }}
                style={{
                  width: 320, padding: '10px 14px',
                  background: '#fff', borderRadius: 12,
                  border: '1px solid #c7d2fe',
                  boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  animation: 'chatToastIn 0.3s ease',
                }}
              >
                <ChatBubbleOutlineIcon style={{ fontSize: 18, color: '#4f46e5', marginTop: 2 }} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.74rem', fontWeight: 800, color: '#0f172a' }}>
                    💬 {entry?.driverName || `Motorista #${t.driverId}`}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.body}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setChatToasts(prev => prev.filter(x => x.id !== t.id)); }} style={{ padding: 2 }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Som de notificação — gerado via Web Audio API (oscilador 880Hz,
          250ms). audioRef guarda o AudioContext partilhado. Sem assets. */}
    </Box>
  );
}

export default Header;
